// Twenty candidate crowns for the Royale winner's head (the crown lab,
// pages/debug/crown-lab). The ones picked are drawn in play by head-styles.ts
// ("viking"); the rest stay for the lab and are left out of the game's bundle.
//
// Same contract as a case in head-styles.ts drawHeadStyle: the caller has
// already done ctx.save() + ctx.translate(headX, headY) and restores after, so
// every draw works around (0, 0).
//   r     : head radius in canvas px (headStyleRadius(4, "crown") * scale)
//   dir   : worm heading in radians
//   t     : seconds, for the calm idle motion (sparkle, shimmer, bob)
//   color : the worm's colour, used as an accent (head-styles doesn't pass
//           one today; wiring a pick in means adding that argument)
//
// Every design ROTATES WITH THE HEAD, like today's crown and the other heads:
// each starts with rotate(dir + 90deg), so local -y is "forward" and the
// crown's points lead the way. Everything stays inside ~1.7r so it fits the
// renderer's dirty-rect (headStyleRadius * 1.8 + 8).

export type Ctx = CanvasRenderingContext2D;
export type CrownDraw = (ctx: Ctx, r: number, dir: number, t: number, color: string) => void;
export interface CrownDesign { name: string; idea: string; draw: CrownDraw }

export const TAU = Math.PI * 2;
export const HALF = Math.PI / 2;
export const INK = "#4a3200"; // dark gold outline: keeps gold readable on a yellow trail
export const GOLD = "#ffd23f";
export const GOLD_DARK = "#c98a12";
export const PEARL = "#fff6c9";
export const RUBY = "#ff4d6d";
export const SAPPHIRE = "#3fd6ff";
export const EMERALD = "#38e08a";

function rgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function mix(a: string, b: string, k: number): string {
  const x = rgb(a), y = rgb(b);
  const c = x.map((v, i) => Math.round(v + (y[i] - v) * k));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}
export const lighten = (c: string, k: number) => mix(c, "#ffffff", k);
export const darken = (c: string, k: number) => mix(c, "#000000", k);
// line widths scale with the head but never vanish at game size
export const lw = (r: number, k: number) => Math.max(0.6, r * k);

export function goldGrad(ctx: Ctx, y0: number, y1: number): CanvasGradient {
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  g.addColorStop(0, "#fff0a8"); g.addColorStop(0.5, GOLD); g.addColorStop(1, GOLD_DARK);
  return g;
}
export function goldRadial(ctx: Ctx, r0: number, r1: number): CanvasGradient {
  const g = ctx.createRadialGradient(0, 0, r0, 0, 0, r1);
  g.addColorStop(0, "#fff3b0"); g.addColorStop(0.55, GOLD); g.addColorStop(1, GOLD_DARK);
  return g;
}
export function disc(ctx: Ctx, x: number, y: number, rad: number, fill: string | CanvasGradient) {
  ctx.fillStyle = fill;
  ctx.beginPath(); ctx.arc(x, y, rad, 0, TAU); ctx.fill();
}
// The worm's own head under a worn crown (top-down designs).
export function face(ctx: Ctx, r: number, color: string, k = 0.92) {
  disc(ctx, 0, 0, r * k, color);
  ctx.strokeStyle = darken(color, 0.45); ctx.lineWidth = lw(r, 0.08);
  ctx.stroke();
}
export function starPath(ctx: Ctx, x: number, y: number, R: number, rin: number, n: number, rot: number) {
  ctx.beginPath();
  for (let i = 0; i < n * 2; i++) {
    const a = rot + (i * Math.PI) / n;
    const d = i % 2 ? rin : R;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
    else ctx.lineTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
  }
  ctx.closePath();
}
// A four-point glint; alpha 0 draws nothing.
export function sparkle(ctx: Ctx, x: number, y: number, s: number, alpha: number) {
  if (alpha <= 0.01) return;
  ctx.save();
  ctx.globalAlpha *= Math.min(1, alpha);
  ctx.fillStyle = "#ffffff";
  starPath(ctx, x, y, s, s * 0.22, 4, -HALF);
  ctx.fill();
  ctx.restore();
}
// Front-view crown outline: a band from y=base up, tips spread evenly from -w
// to w (heights in units of r, negative is forward), valleys between them.
function crownPath(ctx: Ctx, r: number, w: number, base: number, tips: number[], valley: number) {
  const n = tips.length;
  const step = (2 * w) / (n - 1);
  ctx.beginPath();
  ctx.moveTo(-w, base);
  for (let i = 0; i < n; i++) {
    const x = -w + step * i;
    if (i > 0) ctx.lineTo(x - step / 2, valley);
    ctx.lineTo(x, tips[i] * r);
  }
  ctx.lineTo(w, base);
  ctx.closePath();
}
export function band(ctx: Ctx, r: number, w: number, y: number, h: number, fill: string | CanvasGradient = "#b9780a") {
  ctx.fillStyle = fill;
  ctx.fillRect(-w, y, w * 2, h);
  ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.1);
  ctx.strokeRect(-w, y, w * 2, h);
}

// 1. Circlet: a slim gold ring worn on the head, seen from above, five gems
// with a ruby at the front and one glint running round it.
const circlet: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color);
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.34;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.72, 0, TAU); ctx.stroke();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.2;
  ctx.stroke();
  ctx.strokeStyle = "rgba(255,250,210,.7)"; ctx.lineWidth = r * 0.05;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.76, Math.PI * 1.05, Math.PI * 1.6); ctx.stroke();
  const gems = [RUBY, SAPPHIRE, EMERALD, EMERALD, SAPPHIRE];
  for (let i = 0; i < 5; i++) {
    const a = -HALF + (i * TAU) / 5;
    const gx = Math.cos(a) * r * 0.72, gy = Math.sin(a) * r * 0.72;
    disc(ctx, gx, gy, r * (i === 0 ? 0.22 : 0.14), INK);
    disc(ctx, gx, gy, r * (i === 0 ? 0.16 : 0.1), gems[i]);
  }
  const ga = t * 1.1;
  sparkle(ctx, Math.cos(ga) * r * 0.72, Math.sin(ga) * r * 0.72, r * 0.32, 0.5 + 0.5 * Math.sin(t * 3));
};

// 2. Spires: a tall jagged seven-spike crown, the middle spike tallest, pearls
// on the high points and a slow shine sweeping across.
const spires: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const w = r * 1.05, base = r * 0.8;
  crownPath(ctx, r, w, base, [-0.7, -1.05, -0.65, -1.5, -0.65, -1.05, -0.7], -r * 0.05);
  ctx.fillStyle = goldGrad(ctx, -r * 1.5, base); ctx.fill();
  ctx.save();
  ctx.clip();
  const sx = ((t * 0.3) % 1) * w * 5 - w * 2.5;
  ctx.fillStyle = "rgba(255,255,255,.4)";
  ctx.beginPath();
  ctx.moveTo(sx, -r * 1.6); ctx.lineTo(sx + r * 0.3, -r * 1.6); ctx.lineTo(sx - r * 0.5, base); ctx.lineTo(sx - r * 0.8, base);
  ctx.closePath(); ctx.fill();
  ctx.restore();
  ctx.lineJoin = "miter"; ctx.miterLimit = 3;
  ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.11); ctx.stroke();
  band(ctx, r, w, base - r * 0.34, r * 0.34);
  disc(ctx, 0, base - r * 0.17, r * 0.12, RUBY);
  disc(ctx, -w * 0.55, base - r * 0.17, r * 0.09, SAPPHIRE);
  disc(ctx, w * 0.55, base - r * 0.17, r * 0.09, SAPPHIRE);
  for (const [x, y] of [[-w * 2 / 3, -1.05], [0, -1.5], [w * 2 / 3, -1.05]]) disc(ctx, x, y * r, r * 0.11, PEARL);
};

// 3. Laurel: a gold victor's wreath around the head, open at the front, tied
// with a ribbon in the worm's colour behind; a shine rolls along the leaves.
const laurel: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color, 0.8);
  ctx.lineWidth = lw(r, 0.05); ctx.strokeStyle = "#6b4a00";
  for (const s of [-1, 1]) {
    for (let k = 0; k < 7; k++) {
      const a = HALF - s * (0.3 + k * 0.33);
      const tang = a - s * HALF; // leaves point forward along the branch
      const lit = 0.5 + 0.5 * Math.sin(t * 1.6 - k * 0.7);
      for (const [rad, tilt] of [[0.68, -0.45], [0.98, 0.45]]) {
        ctx.save();
        ctx.translate(Math.cos(a) * r * rad, Math.sin(a) * r * rad);
        ctx.rotate(tang + s * tilt);
        ctx.fillStyle = mix("#d9a514", "#fff1a0", lit * 0.6);
        ctx.beginPath(); ctx.ellipse(0, 0, r * 0.3, r * 0.13, 0, 0, TAU); ctx.fill(); ctx.stroke();
        ctx.restore();
      }
    }
  }
  // the ribbon knot at the back
  ctx.fillStyle = lighten(color, 0.1); ctx.strokeStyle = darken(color, 0.5); ctx.lineWidth = lw(r, 0.06);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(0, r * 0.82); ctx.lineTo(s * r * 0.32, r * 1.38); ctx.lineTo(s * r * 0.12, r * 1.3); ctx.closePath();
    ctx.fill(); ctx.stroke();
  }
  disc(ctx, 0, r * 0.84, r * 0.15, lighten(color, 0.1)); ctx.stroke();
};

// 4. Halo: a gold ring floating just ahead of the head with little points on
// its far rim, glowing and bobbing gently.
const halo: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color);
  ctx.save();
  ctx.translate(0, -r * 0.2 + Math.sin(t * 1.8) * r * 0.08);
  const rx = r * 1.02, ry = r * 0.42;
  ctx.shadowColor = "rgba(255,215,90,.85)"; ctx.shadowBlur = r * 0.7;
  ctx.fillStyle = "#ffe27a";
  for (let k = 0; k < 7; k++) {
    const a = Math.PI + ((k + 0.5) * Math.PI) / 7;
    const c = Math.cos(a), s = Math.sin(a);
    const bx = c * rx, by = s * ry;
    const tx = -s * rx, ty = c * ry; // tangent
    const tl = Math.hypot(tx, ty);
    const ux = (tx / tl) * r * 0.11, uy = (ty / tl) * r * 0.11;
    const len = k === 3 ? 0.42 : 0.28;
    ctx.beginPath();
    ctx.moveTo(bx - ux, by - uy);
    ctx.lineTo(c * (rx + r * len), s * (ry + r * len));
    ctx.lineTo(bx + ux, by + uy);
    ctx.closePath(); ctx.fill();
  }
  ctx.strokeStyle = "#ffd96a"; ctx.lineWidth = r * 0.18;
  ctx.beginPath(); ctx.ellipse(0, 0, rx, ry, 0, 0, TAU); ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "rgba(255,255,240,.9)"; ctx.lineWidth = r * 0.06;
  ctx.beginPath(); ctx.ellipse(0, 0, rx, ry, 0, Math.PI * 1.1, Math.PI * 1.9); ctx.stroke();
  ctx.restore();
};

// 5. Jester: three floppy points (two in the worm's colour, one gold) on a
// gold band, a bell on each tip swinging a little out of step.
const jester: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  const base = r * 0.3;
  ctx.lineJoin = "round"; ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.09);
  const pts: [number, number, number, string][] = [
    [-1, -1.3, -0.75, color], [1, 1.3, -0.75, color], [0, 0, -1.3, GOLD],
  ];
  for (let i = 0; i < pts.length; i++) {
    const [s, tipX, tipY, fill] = pts[i];
    const sw = Math.sin(t * 2.4 + i * 2.1) * r * 0.12;
    const tx = tipX * r + sw, ty = tipY * r;
    const x0 = s === 0 ? -r * 0.38 : s * r * 0.95, x1 = s === 0 ? r * 0.38 : s * r * 0.25;
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(x0, base);
    ctx.quadraticCurveTo(x0 + (s === 0 ? -r * 0.1 : s * r * 0.15), ty * 0.3, tx, ty);
    ctx.quadraticCurveTo((x1 + tx) / 2 + (s === 0 ? r * 0.2 : -s * r * 0.15), (base + ty) / 2, x1, base);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    disc(ctx, tx, ty, r * 0.19, GOLD); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(tx - r * 0.09, ty + r * 0.05); ctx.lineTo(tx + r * 0.09, ty + r * 0.05); ctx.stroke();
  }
  ctx.fillStyle = goldGrad(ctx, base - r * 0.1, r * 0.8);
  ctx.beginPath(); ctx.roundRect(-r, base - r * 0.08, r * 2, r * 0.55, r * 0.15); ctx.fill(); ctx.stroke();
  for (const x of [-0.55, 0, 0.55]) {
    ctx.fillStyle = x === 0 ? RUBY : darken(color, 0.2);
    ctx.beginPath();
    ctx.moveTo(x * r, base - r * 0.02); ctx.lineTo(x * r + r * 0.13, base + r * 0.2); ctx.lineTo(x * r, base + r * 0.42); ctx.lineTo(x * r - r * 0.13, base + r * 0.2);
    ctx.closePath(); ctx.fill();
  }
};

// 6. Imperial: the classic royal crown: velvet cap in the worm's colour under
// gold arches, a jewelled band and a cross-topped orb that catches the light.
const imperial: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  const w = r * 0.95, base = r * 0.85, bandTop = r * 0.42;
  ctx.fillStyle = darken(color, 0.15); ctx.strokeStyle = darken(color, 0.6); ctx.lineWidth = lw(r, 0.08);
  ctx.beginPath();
  ctx.moveTo(-w * 0.92, bandTop); ctx.bezierCurveTo(-w * 0.95, -r * 0.78, w * 0.95, -r * 0.78, w * 0.92, bandTop);
  ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.lineCap = "round";
  const arches = () => {
    ctx.beginPath();
    ctx.moveTo(-w * 0.9, bandTop); ctx.quadraticCurveTo(-w * 0.98, -r * 0.62, 0, -r * 0.64);
    ctx.quadraticCurveTo(w * 0.98, -r * 0.62, w * 0.9, bandTop);
    ctx.moveTo(0, bandTop); ctx.lineTo(0, -r * 0.64);
    ctx.stroke();
  };
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.32; arches();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.19; arches();
  disc(ctx, -w * 0.62, -r * 0.33, r * 0.09, PEARL);
  disc(ctx, w * 0.62, -r * 0.33, r * 0.09, PEARL);
  disc(ctx, 0, -r * 0.1, r * 0.09, PEARL);
  band(ctx, r, w, bandTop, base - bandTop, goldGrad(ctx, bandTop, base));
  disc(ctx, 0, (bandTop + base) / 2, r * 0.15, RUBY);
  disc(ctx, -w * 0.58, (bandTop + base) / 2, r * 0.11, EMERALD);
  disc(ctx, w * 0.58, (bandTop + base) / 2, r * 0.11, EMERALD);
  // orb and cross
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.24;
  ctx.beginPath(); ctx.moveTo(0, -r * 1.05); ctx.lineTo(0, -r * 1.58); ctx.moveTo(-r * 0.18, -r * 1.38); ctx.lineTo(r * 0.18, -r * 1.38); ctx.stroke();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.12; ctx.stroke();
  ctx.save(); ctx.translate(0, -r * 0.9);
  disc(ctx, 0, 0, r * 0.27, INK);
  disc(ctx, 0, 0, r * 0.2, goldRadial(ctx, 0, r * 0.22));
  sparkle(ctx, -r * 0.07, -r * 0.07, r * 0.28, 0.5 + 0.5 * Math.sin(t * 2.2));
  ctx.restore();
};

// 7. Tiara: a fine gold arc on the front of the head with little loops and a
// big sapphire teardrop leading the way, twinkling.
const tiara: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color);
  const a0 = -HALF - 1.15, a1 = -HALF + 1.15;
  ctx.lineCap = "round";
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.28;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.84, a0, a1); ctx.stroke();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.16; ctx.stroke();
  for (const k of [-2, -1, 1, 2]) {
    const a = -HALF + k * 0.44;
    const x = Math.cos(a) * r * 1.08, y = Math.sin(a) * r * 1.08;
    ctx.strokeStyle = INK; ctx.lineWidth = r * 0.15;
    ctx.beginPath(); ctx.arc(x, y, r * 0.15, 0, TAU); ctx.stroke();
    ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.07; ctx.stroke();
    disc(ctx, x, y, r * 0.06, PEARL);
  }
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.66);
  ctx.bezierCurveTo(r * 0.34, -r * 1.3, r * 0.3, -r * 0.84, 0, -r * 0.82);
  ctx.bezierCurveTo(-r * 0.3, -r * 0.84, -r * 0.34, -r * 1.3, 0, -r * 1.66);
  const g = ctx.createLinearGradient(0, -r * 1.66, 0, -r * 0.82);
  g.addColorStop(0, "#c9f4ff"); g.addColorStop(0.5, "#5ab8ff"); g.addColorStop(1, "#1d4fc4");
  ctx.fillStyle = g; ctx.fill();
  ctx.strokeStyle = GOLD_DARK; ctx.lineWidth = lw(r, 0.1); ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,.6)";
  ctx.beginPath(); ctx.ellipse(-r * 0.08, -r * 1.2, r * 0.05, r * 0.14, 0.3, 0, TAU); ctx.fill();
  sparkle(ctx, r * 0.12, -r * 1.3, r * 0.3, Math.max(0, Math.sin(t * 2.5)));
};

// 8. Blaze: five flame points rising from a gold band, flickering slowly with
// a warm glow; the crown is on fire, the head isn't.
const blaze: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const w = r * 0.95, foot = r * 0.42;
  const lift = [0.1, 0.35, 0.65, 0.35, 0.1];
  const flame = (i: number, k: number) => {
    const x = (-1 + i * 0.5) * w * 0.78;
    const h = r * (0.75 + lift[i] + 0.12 * Math.sin(t * 4 + i * 1.9)) * k;
    const lean = r * 0.12 * Math.sin(t * 2.6 + i);
    const hw = r * 0.27 * k;
    ctx.beginPath();
    ctx.moveTo(x - hw, foot);
    ctx.quadraticCurveTo(x - hw * 1.15, foot - h * 0.55, x + lean, foot - h);
    ctx.quadraticCurveTo(x + hw * 1.15, foot - h * 0.55, x + hw, foot);
    ctx.closePath();
  };
  ctx.shadowColor = "rgba(255,130,40,.8)"; ctx.shadowBlur = r * 0.6;
  ctx.fillStyle = "#ff7a1a"; ctx.strokeStyle = "#7a2a12"; ctx.lineWidth = lw(r, 0.08); ctx.lineJoin = "round";
  for (const i of [0, 4, 1, 3, 2]) { flame(i, 1); ctx.fill(); }
  ctx.shadowBlur = 0;
  for (const i of [0, 4, 1, 3, 2]) { flame(i, 1); ctx.stroke(); }
  ctx.fillStyle = "#ffd23f";
  for (let i = 0; i < 5; i++) { flame(i, 0.55); ctx.fill(); }
  ctx.fillStyle = goldGrad(ctx, foot - r * 0.08, r * 0.85);
  ctx.beginPath(); ctx.roundRect(-w, foot - r * 0.08, w * 2, r * 0.48, r * 0.08); ctx.fill();
  ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.1); ctx.stroke();
  disc(ctx, 0, foot + r * 0.16, r * 0.12, "#ff3b2f");
};

// 9. Frost: a fan of faceted ice shards (lit face and shadow face) on a
// frosted band, a glint hopping from tip to tip.
const frost: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const angles = [-0.64, -0.32, 0, 0.32, 0.64];
  const lens = [0.95, 1.25, 1.62, 1.25, 0.95];
  const ox = 0, oy = r * 0.7;
  const tips: [number, number][] = [];
  ctx.lineJoin = "round";
  for (const i of [0, 4, 1, 3, 2]) {
    const a = angles[i];
    const dx = Math.sin(a), dy = -Math.cos(a);
    const px = Math.cos(a), py = Math.sin(a);
    const hw = r * 0.18, L = lens[i] * r;
    const bx = ox + dx * r * 0.3, by = oy + dy * r * 0.3;
    const sx = bx + dx * L * 0.76, sy = by + dy * L * 0.76;
    const tx = bx + dx * L, ty = by + dy * L;
    tips[i] = [tx, ty];
    ctx.fillStyle = "#e9fcff";
    ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx - px * hw, by - py * hw); ctx.lineTo(sx - px * hw, sy - py * hw); ctx.lineTo(tx, ty); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#79c8ec";
    ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + px * hw, by + py * hw); ctx.lineTo(sx + px * hw, sy + py * hw); ctx.lineTo(tx, ty); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = "#1f5f8a"; ctx.lineWidth = lw(r, 0.07);
    ctx.beginPath(); ctx.moveTo(bx - px * hw, by - py * hw); ctx.lineTo(sx - px * hw, sy - py * hw); ctx.lineTo(tx, ty); ctx.lineTo(sx + px * hw, sy + py * hw); ctx.lineTo(bx + px * hw, by + py * hw); ctx.stroke();
  }
  ctx.fillStyle = "#c4ecff"; ctx.strokeStyle = "#1f5f8a"; ctx.lineWidth = lw(r, 0.08);
  ctx.beginPath(); ctx.roundRect(-r * 0.88, r * 0.42, r * 1.76, r * 0.4, r * 0.12); ctx.fill(); ctx.stroke();
  for (const x of [-0.55, 0, 0.55]) disc(ctx, x * r, r * 0.62, r * (x === 0 ? 0.12 : 0.08), x === 0 ? "#ffffff" : "#7fd8ff");
  const k = t * 0.8;
  const idx = Math.floor(k) % 5;
  sparkle(ctx, tips[idx][0], tips[idx][1], r * 0.36, Math.sin((k % 1) * Math.PI));
};

// 10. Neon: just the outline of a five-point crown, lit up in the worm's own
// colour with a gold band line, breathing slowly.
const neon: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  const w = r * 1.0, base = r * 0.75;
  const pulse = 0.75 + 0.25 * Math.sin(t * 2.2);
  crownPath(ctx, r, w, base, [-0.6, -0.95, -1.25, -0.95, -0.6], -r * 0.05);
  ctx.fillStyle = "rgba(8,12,24,.85)"; ctx.fill();
  ctx.lineJoin = "round";
  ctx.shadowColor = color; ctx.shadowBlur = r * 0.9 * pulse;
  ctx.strokeStyle = lighten(color, 0.2); ctx.lineWidth = r * 0.2; ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = `rgba(255,255,255,${(0.6 + 0.3 * pulse).toFixed(3)})`; ctx.lineWidth = r * 0.06; ctx.stroke();
  ctx.shadowColor = "rgba(255,210,63,.9)"; ctx.shadowBlur = r * 0.6 * pulse;
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.12; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(-w * 0.72, base - r * 0.32); ctx.lineTo(w * 0.72, base - r * 0.32); ctx.stroke();
  ctx.shadowBlur = 0;
  for (const x of [-1, -0.5, 0, 0.5, 1]) {
    const y = [-0.6, -0.95, -1.25, -0.95, -0.6][(x + 1) * 2] * r;
    disc(ctx, x * w, y, r * 0.1, "#ffffff");
  }
};

// 11. Pixel: an 8-bit crown on a 9x7 grid (dark outline pass, then colour),
// its three pearls blinking in turn like an arcade sprite.
const PIXEL_MAP = [
  "p...p...p",
  "#...#...#",
  "##.#h#.##",
  "#hh###hh#",
  "#r##b##r#",
  "#########",
  "ddddddddd",
];
const PIXEL_COLOURS: Record<string, string> = { "#": GOLD, h: "#fff0a8", d: GOLD_DARK, r: RUBY, b: SAPPHIRE, p: PEARL };
const pixel: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const p = r * 0.24;
  const ox = -4.5 * p, oy = -4.4 * p;
  const lit = Math.floor(t * 2) % 3;
  const o = p * 0.28;
  ctx.fillStyle = "#2a1a00";
  PIXEL_MAP.forEach((row, y) => [...row].forEach((c, x) => {
    if (c !== ".") ctx.fillRect(ox + x * p - o, oy + y * p - o, p + o * 2, p + o * 2);
  }));
  PIXEL_MAP.forEach((row, y) => [...row].forEach((c, x) => {
    if (c === ".") return;
    let fill = PIXEL_COLOURS[c];
    if (c === "p") fill = Math.floor(x / 4) === lit ? "#ffffff" : "#e0c870";
    ctx.fillStyle = fill;
    // a hair of overlap so the pixels don't show seams when rotated
    ctx.fillRect(ox + x * p - 0.2, oy + y * p - 0.2, p + 0.4, p + 0.4);
  }));
};

// 12. Viking: a steel helm with a gold band and nose ridge, two ivory horns
// sweeping forward.
export const viking: CrownDraw = (ctx, r, dir) => {
  ctx.rotate(dir + HALF);
  ctx.lineJoin = "round";
  for (const s of [-1, 1]) {
    const g = ctx.createLinearGradient(s * r * 0.7, 0, s * r * 1.3, -r * 1.25);
    g.addColorStop(0, "#f6ecd2"); g.addColorStop(0.7, "#e2cf9f"); g.addColorStop(1, "#7d6844");
    ctx.fillStyle = g; ctx.strokeStyle = "#4e3f22"; ctx.lineWidth = lw(r, 0.08);
    ctx.beginPath();
    ctx.moveTo(s * r * 0.72, r * 0.15);
    ctx.quadraticCurveTo(s * r * 1.6, -r * 0.05, s * r * 1.28, -r * 1.25);
    ctx.quadraticCurveTo(s * r * 1.22, -r * 0.4, s * r * 0.6, -r * 0.42);
    ctx.closePath(); ctx.fill(); ctx.stroke();
  }
  const steel = ctx.createLinearGradient(0, -r * 0.9, 0, r * 0.4);
  steel.addColorStop(0, "#eef3f9"); steel.addColorStop(1, "#7f8da2");
  ctx.fillStyle = steel; ctx.strokeStyle = "#3e4756"; ctx.lineWidth = lw(r, 0.09);
  ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.92, Math.PI, TAU); ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.fillStyle = goldGrad(ctx, -r * 0.62, r * 0.3); ctx.strokeStyle = INK;
  ctx.beginPath(); ctx.rect(-r * 0.12, -r * 0.62, r * 0.24, r * 0.9); ctx.fill(); ctx.stroke();
  band(ctx, r, r * 0.98, r * 0.2, r * 0.48, goldGrad(ctx, r * 0.2, r * 0.68));
  for (const x of [-0.7, -0.35, 0.35, 0.7]) disc(ctx, x * r, r * 0.44, r * 0.07, INK);
  disc(ctx, 0, r * 0.44, r * 0.13, RUBY);
};

// 13. Starlit: a band with five posts, each topped by a star; the centre star
// is the worm's colour and the stars twinkle out of step.
const starlit: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  const w = r * 0.95;
  const xs = [-0.82, -0.42, 0, 0.42, 0.82], ys = [-0.15, -0.6, -0.95, -0.6, -0.15];
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.22; ctx.lineCap = "round";
  ctx.beginPath();
  for (let i = 0; i < 5; i++) { ctx.moveTo(xs[i] * w, r * 0.4); ctx.lineTo(xs[i] * w, ys[i] * r); }
  ctx.stroke();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.1; ctx.stroke();
  band(ctx, r, w, r * 0.32, r * 0.46, goldGrad(ctx, r * 0.32, r * 0.78));
  ctx.lineJoin = "round"; ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.08);
  for (let i = 0; i < 5; i++) {
    const big = i === 2;
    const R = r * (big ? 0.44 : 0.28) * (1 + 0.08 * Math.sin(t * 2.4 + i * 1.7));
    starPath(ctx, xs[i] * w, ys[i] * r, R, R * 0.45, 5, -HALF);
    ctx.fillStyle = big ? lighten(color, 0.1) : GOLD;
    ctx.fill(); ctx.stroke();
  }
  disc(ctx, 0, r * 0.55, r * 0.1, PEARL);
};

// 14. Constellation: a slim gold circlet with six little stars orbiting the
// head, each softly pulsing.
const constellation: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  face(ctx, r, color, 0.88);
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.26;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.68, 0, TAU); ctx.stroke();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.14; ctx.stroke();
  ctx.strokeStyle = "rgba(255,226,122,.35)"; ctx.lineWidth = lw(r, 0.04);
  ctx.beginPath(); ctx.arc(0, 0, r * 1.3, 0, TAU); ctx.stroke();
  ctx.lineJoin = "round";
  for (let i = 0; i < 6; i++) {
    const a = t * 0.8 + (i * TAU) / 6;
    const x = Math.cos(a) * r * 1.3, y = Math.sin(a) * r * 1.3;
    const R = r * (0.24 + 0.06 * Math.sin(t * 3 + i * 1.3));
    starPath(ctx, x, y, R, R * 0.42, 5, a);
    ctx.fillStyle = "#ffe27a"; ctx.fill();
    ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.05); ctx.stroke();
  }
  disc(ctx, 0, -r * 0.68, r * 0.13, RUBY);
};

// 15. Cat ears: two gold ear-points with ruby insides on the head, a small
// band with a gem between them; now and then an ear twitches.
const catEars: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  ctx.lineJoin = "round";
  for (const s of [-1, 1]) {
    const a0 = -HALF + s * 0.78;
    const ph = (t + (s > 0 ? 2.1 : 0)) % 4.2;
    const tw = ph < 0.3 ? Math.sin((ph / 0.3) * Math.PI) * 0.22 * s : 0;
    const at = a0 + tw;
    const pt = (a: number, d: number): [number, number] => [Math.cos(a) * r * d, Math.sin(a) * r * d];
    let p = [pt(a0 - 0.45, 0.8), pt(at, 1.62), pt(a0 + 0.45, 0.8)];
    ctx.fillStyle = goldGrad(ctx, -r * 1.6, 0); ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.1);
    ctx.beginPath(); ctx.moveTo(p[0][0], p[0][1]); ctx.lineTo(p[1][0], p[1][1]); ctx.lineTo(p[2][0], p[2][1]); ctx.closePath(); ctx.fill(); ctx.stroke();
    p = [pt(a0 - 0.22, 0.95), pt(at, 1.38), pt(a0 + 0.22, 0.95)];
    ctx.fillStyle = "#ff6b8a";
    ctx.beginPath(); ctx.moveTo(p[0][0], p[0][1]); ctx.lineTo(p[1][0], p[1][1]); ctx.lineTo(p[2][0], p[2][1]); ctx.closePath(); ctx.fill();
  }
  face(ctx, r, color);
  ctx.lineCap = "round";
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.26;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.74, -HALF - 0.95, -HALF + 0.95); ctx.stroke();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.13; ctx.stroke();
  disc(ctx, 0, -r * 0.74, r * 0.17, INK);
  disc(ctx, 0, -r * 0.74, r * 0.12, SAPPHIRE);
};

// 16. Kintsugi: an ancient, chipped stone crown with moss at its foot, its
// cracks mended in gold that glows softly.
const kintsugi: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const w = r;
  const outline = [
    [-1, 0.8], [-1, -0.45], [-0.82, -0.66], [-0.62, -0.42], [-0.46, -0.1], [-0.3, -0.76], [-0.12, -1.1],
    [0.04, -0.98], [0.2, -1.04], [0.32, -0.62], [0.48, -0.12], [0.64, -0.52], [0.84, -0.68], [1, -0.42], [1, 0.8],
  ];
  ctx.beginPath();
  outline.forEach(([x, y], i) => (i ? ctx.lineTo(x * w, y * r) : ctx.moveTo(x * w, y * r)));
  ctx.closePath();
  const g = ctx.createLinearGradient(0, -r * 1.1, 0, r * 0.8);
  g.addColorStop(0, "#c2bdb1"); g.addColorStop(1, "#6a665d");
  ctx.fillStyle = g; ctx.fill();
  ctx.save(); ctx.clip();
  for (const [x, y, s] of [[-0.6, 0.1, 0.12], [0.3, 0.45, 0.1], [0.7, 0.05, 0.08], [-0.2, -0.5, 0.07], [0.1, -0.2, 0.06]]) {
    disc(ctx, x * r, y * r, s * r, "rgba(40,36,30,.25)");
  }
  ctx.fillStyle = "#5c8a3a";
  ctx.beginPath(); ctx.ellipse(-r * 0.8, r * 0.78, r * 0.45, r * 0.22, 0, 0, TAU); ctx.fill();
  ctx.beginPath(); ctx.ellipse(r * 0.55, r * 0.82, r * 0.35, r * 0.16, 0, 0, TAU); ctx.fill();
  ctx.restore();
  ctx.strokeStyle = "#2f2d29"; ctx.lineWidth = lw(r, 0.1); ctx.lineJoin = "round"; ctx.stroke();
  ctx.strokeStyle = "rgba(0,0,0,.35)"; ctx.lineWidth = lw(r, 0.07);
  ctx.beginPath(); ctx.moveTo(-w * 0.95, r * 0.35); ctx.lineTo(w * 0.95, r * 0.35); ctx.stroke();
  const cracks = () => {
    ctx.beginPath();
    ctx.moveTo(-0.14 * r, -1.02 * r); ctx.lineTo(-0.04 * r, -0.62 * r); ctx.lineTo(-0.22 * r, -0.3 * r); ctx.lineTo(-0.04 * r, 0.1 * r); ctx.lineTo(-0.16 * r, 0.5 * r);
    ctx.moveTo(-0.04 * r, -0.62 * r); ctx.lineTo(0.2 * r, -0.45 * r);
    ctx.moveTo(0.6 * r, -0.45 * r); ctx.lineTo(0.46 * r, -0.05 * r); ctx.lineTo(0.64 * r, 0.3 * r);
    ctx.moveTo(-0.85 * r, -0.4 * r); ctx.lineTo(-0.7 * r, -0.05 * r);
    ctx.stroke();
  };
  ctx.lineCap = "round";
  ctx.strokeStyle = "#1e1c19"; ctx.lineWidth = r * 0.15; cracks();
  const glow = 0.6 + 0.4 * Math.sin(t * 1.5);
  ctx.shadowColor = `rgba(255,200,70,${glow.toFixed(3)})`; ctx.shadowBlur = r * 0.5;
  ctx.strokeStyle = "#ffcf4d"; ctx.lineWidth = r * 0.075; cracks();
  ctx.shadowBlur = 0;
};

// 17. Sunburst: twelve gold rays around the head, long and short, turning
// very slowly like a sun disc.
const sunburst: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  ctx.save();
  ctx.rotate(t * 0.25);
  ctx.fillStyle = goldRadial(ctx, r * 0.6, r * 1.55); ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.07); ctx.lineJoin = "round";
  ctx.beginPath();
  for (let i = 0; i < 12; i++) {
    const a = (i * TAU) / 12;
    const L = r * (i % 2 ? 1.2 : 1.55);
    ctx.moveTo(Math.cos(a - 0.17) * r * 0.8, Math.sin(a - 0.17) * r * 0.8);
    ctx.lineTo(Math.cos(a) * L, Math.sin(a) * L);
    ctx.lineTo(Math.cos(a + 0.17) * r * 0.8, Math.sin(a + 0.17) * r * 0.8);
    ctx.closePath();
  }
  ctx.fill(); ctx.stroke();
  ctx.restore();
  disc(ctx, 0, 0, r * 0.9, INK);
  disc(ctx, 0, 0, r * 0.84, goldRadial(ctx, r * 0.3, r * 0.9));
  face(ctx, r, color, 0.62);
};

// 18. Simple: a clean flat three-point crown, no detail at all, with a soft
// gold glow that breathes. Reads at any size.
const simple: CrownDraw = (ctx, r, dir, t) => {
  ctx.rotate(dir + HALF);
  const w = r * 0.95, base = r * 0.72;
  crownPath(ctx, r, w, base, [-0.55, -1.12, -0.55], r * 0.02);
  ctx.lineJoin = "round";
  ctx.shadowColor = "rgba(255,210,63,.8)"; ctx.shadowBlur = r * (0.7 + 0.25 * Math.sin(t * 1.8));
  ctx.fillStyle = GOLD; ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.18; ctx.stroke();
  ctx.fillStyle = "#e0a91a";
  ctx.fillRect(-w, base - r * 0.26, w * 2, r * 0.26);
};

// 19. Pennant: a small gold crown with a pole on its middle point flying a
// pennant in the worm's colour, rippling.
const pennant: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  const w = r * 0.9, base = r * 0.78;
  const top = -r * 1.62;
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.12; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(0, -r * 0.6); ctx.lineTo(0, top); ctx.stroke();
  const L = r * 1.05, h0 = r * 0.46;
  const wave = (u: number) => Math.sin(u * 3.2 - t * 3.5) * r * 0.13 * u;
  ctx.beginPath();
  for (let k = 0; k <= 10; k++) { const u = k / 10; ctx.lineTo(u * L, top + wave(u)); }
  for (let k = 10; k >= 0; k--) { const u = k / 10; ctx.lineTo(u * L, top + h0 * (1 - u) + wave(u)); }
  ctx.closePath();
  ctx.fillStyle = color; ctx.fill();
  ctx.strokeStyle = darken(color, 0.5); ctx.lineWidth = lw(r, 0.06); ctx.lineJoin = "round"; ctx.stroke();
  disc(ctx, 0, top, r * 0.12, GOLD);
  crownPath(ctx, r, w, base, [-0.35, -0.72, -0.35], r * 0.1);
  ctx.fillStyle = goldGrad(ctx, -r * 0.72, base); ctx.fill();
  ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.11); ctx.stroke();
  band(ctx, r, w, base - r * 0.3, r * 0.3);
  disc(ctx, 0, base - r * 0.15, r * 0.1, RUBY);
};

// 20. Coronet: a crown seen from straight above: an eight-point gold ring
// round a velvet cap in the worm's colour, crossed arches, a ruby on top,
// pearls lighting up one after another.
const coronet: CrownDraw = (ctx, r, dir, t, color) => {
  ctx.rotate(dir + HALF);
  starPath(ctx, 0, 0, r * 1.32, r * 0.9, 8, -HALF);
  ctx.fillStyle = goldRadial(ctx, r * 0.7, r * 1.35); ctx.fill();
  ctx.strokeStyle = INK; ctx.lineWidth = lw(r, 0.09); ctx.lineJoin = "round"; ctx.stroke();
  disc(ctx, 0, 0, r * 0.82, darken(color, 0.1));
  ctx.strokeStyle = INK; ctx.stroke();
  ctx.lineCap = "round";
  const cross = () => { ctx.beginPath(); ctx.moveTo(0, -r * 0.8); ctx.lineTo(0, r * 0.8); ctx.moveTo(-r * 0.8, 0); ctx.lineTo(r * 0.8, 0); ctx.stroke(); };
  ctx.strokeStyle = INK; ctx.lineWidth = r * 0.28; cross();
  ctx.strokeStyle = GOLD; ctx.lineWidth = r * 0.15; cross();
  const lit = Math.floor(t * 2) % 8;
  for (let i = 0; i < 8; i++) {
    const a = -HALF + (i * TAU) / 8;
    disc(ctx, Math.cos(a) * r * 1.3, Math.sin(a) * r * 1.3, r * 0.12, i === lit ? "#ffffff" : PEARL);
  }
  disc(ctx, 0, 0, r * 0.25, INK);
  disc(ctx, 0, 0, r * 0.19, RUBY);
  disc(ctx, -r * 0.06, -r * 0.06, r * 0.06, "rgba(255,255,255,.75)");
};

export const CROWN_DESIGNS: CrownDesign[] = [
  { name: "Circlet", idea: "slim gold ring from above, five gems", draw: circlet },
  { name: "Spires", idea: "tall jagged seven-spike crown", draw: spires },
  { name: "Laurel", idea: "victor's gold wreath, ribbon behind", draw: laurel },
  { name: "Halo", idea: "floating glowing ring with points", draw: halo },
  { name: "Jester", idea: "three floppy points with bells", draw: jester },
  { name: "Imperial", idea: "velvet cap, arches, orb and cross", draw: imperial },
  { name: "Tiara", idea: "fine arc with a sapphire teardrop", draw: tiara },
  { name: "Blaze", idea: "flame points on a gold band", draw: blaze },
  { name: "Frost", idea: "faceted ice shards", draw: frost },
  { name: "Neon", idea: "glowing outline in the worm's colour", draw: neon },
  { name: "Pixel", idea: "8-bit sprite crown", draw: pixel },
  { name: "Viking", idea: "steel helm, gold band, ivory horns", draw: viking },
  { name: "Starlit", idea: "five stars on posts", draw: starlit },
  { name: "Constellation", idea: "circlet with orbiting stars", draw: constellation },
  { name: "Cat ears", idea: "gold ear-points that twitch", draw: catEars },
  { name: "Kintsugi", idea: "ancient stone, cracks mended in gold", draw: kintsugi },
  { name: "Sunburst", idea: "slowly turning gold rays", draw: sunburst },
  { name: "Simple", idea: "flat three points with a glow", draw: simple },
  { name: "Pennant", idea: "small crown flying a flag", draw: pennant },
  { name: "Coronet", idea: "crown seen from above", draw: coronet },
];
